/**
 * Direct browser -> S3 upload for PRIVATE video families (BRAVON-6332
 * Phase 2b, decision D10 option B).
 *
 * This portal's own `POST /upload/video` writes the object `public-read` onto
 * the PUBLIC bucket with the portal's own credentials — it never reaches
 * api_games, so it can neither honour an upload `context` nor mint a receipt.
 * A profile video pushed through it would therefore always stage in public,
 * whatever the server's flags say.
 *
 * This module is the narrow way out: it asks api_games (through the portal's
 * `/api/*` Bearer proxy) for presigned urls and sends the bytes straight to
 * S3. When `PRIVATE_VIDEO_UPLOADS_ENABLED` is on, `context: {type}` makes the
 * object land on the private bucket and the response carries
 * `{url: null, private: true, receipt, previewUrl}` — the `receipt` is the
 * only piece that may travel back, inside the video object of the profile
 * save. With the flags off the object lands on the same public bucket the
 * legacy path uses (under `media/videos/` rather than `uploads/videos/`) and
 * the payload is the familiar `{url, thumb, duration}`.
 *
 * Deliberately transport-only: this portal has no Dropzone video surface, so
 * none of the admin portal's Dropzone glue is ported. It is a trimmed sibling
 * of `web_admin_portal/public/js/_helpers/directUploader.js` — keep the two in
 * step.
 *
 * Every failure resolves with NULL rather than throwing: the caller quietly
 * falls back to the legacy portal upload and behaves exactly like today.
 */
import Request from './request';

const INIT_URL = '/api/upload/direct/init';
const COMPLETE_URL = '/api/upload/direct/complete';
const ABORT_URL = '/api/upload/direct/abort';

const API_HEADERS = {
  'Accept-Version': '1.0.0',
  'X-Gamification-API': 'Games',
};

// 4 parts in flight saturates a normal uplink without starving the page.
const PART_CONCURRENCY = 4;
const PART_ATTEMPTS = 3;
const RETRY_BASE_DELAY = 1000;

// Same placeholder the server falls back to when it skips thumbnail
// generation.
const DEFAULT_VIDEO_THUMB =
  'https://bravon-media.s3.eu-central-1.amazonaws.com/media/video_thumb_default.png';

const isSupported = () =>
  !!(
    typeof window !== 'undefined' &&
    window.Promise &&
    window.XMLHttpRequest &&
    window.Blob &&
    window.File &&
    File.prototype.slice
  );

const delay = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * POSTs json to one of the `/api/upload/direct/*` endpoints through the
 * portal proxy (which forwards the session's Bearer token) and resolves with
 * the `data` of the `{success, data}` envelope. `Request` puts the CSRF token
 * in the body, which is what lusca reads.
 */
const api = (_csrf, url, body) =>
  new Promise((resolve, reject) => {
    new Request(_csrf)
      .ajax('POST', window.location.origin + url, body, true, true, API_HEADERS)
      .done(result => resolve(result ? result.data : null))
      .fail(err => reject(err));
  });

/**
 * One PUT to a presigned S3 url. XHR rather than fetch because only XHR
 * reports upload progress.
 */
const put = (url, blob, headers, onBytes, state) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    if (headers) {
      Object.keys(headers).forEach(name =>
        xhr.setRequestHeader(name, headers[name]),
      );
    }
    if (xhr.upload && onBytes) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          onBytes(event.loaded);
        }
      };
    }

    const done = () => {
      const index = state.requests.indexOf(xhr);
      if (index >= 0) {
        state.requests.splice(index, 1);
      }
    };

    xhr.onload = () => {
      done();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
      } else {
        reject(new Error(`S3 answered ${xhr.status}`));
      }
    };
    xhr.onerror = () => {
      done();
      reject(new Error('Network error while uploading to S3'));
    };
    xhr.ontimeout = () => {
      done();
      reject(new Error('Timeout while uploading to S3'));
    };
    xhr.onabort = () => {
      done();
      reject(new Error('Upload aborted'));
    };

    state.requests.push(xhr);
    xhr.send(blob);
  });

/**
 * Merges the private-upload fields of an api_games response into the result.
 * Keyed off `private === true`, which only appears when the object actually
 * landed on the private bucket — otherwise the result is returned untouched.
 */
const withPrivate = (result, source) => {
  if (!source || source.private !== true) {
    return result;
  }
  return {
    ...result,
    // Hard null rather than `source.url`: in single mode `source` is the
    // `init` payload, whose `url` is the presigned PUT and is NOT the
    // object's address.
    url: null,
    private: true,
    receipt: source.receipt || null,
    previewUrl: source.previewUrl || null,
  };
};

const uploadParts = (file, data, state, onLoaded) => {
  const partUrls = data.partUrls || [];
  const { partSize } = data;
  const parts = [];
  const loadedByIndex = [];
  let next = 0;

  const reportLoaded = () => {
    if (onLoaded) {
      onLoaded(loadedByIndex.reduce((sum, bytes) => sum + (bytes || 0), 0));
    }
  };

  const uploadPart = (index, attempt) => {
    const offset = index * partSize;
    const end = Math.min(offset + partSize, file.size);
    const blob = file.slice(offset, end);

    const onBytes = onLoaded
      ? loaded => {
          loadedByIndex[index] = Math.min(loaded, end - offset);
          reportLoaded();
        }
      : null;

    return put(partUrls[index], blob, null, onBytes, state)
      .then(xhr => {
        // Quotes are kept exactly as S3 returns them — CompleteMultipartUpload
        // expects the quoted value. Reading the header at all requires
        // `ExposeHeaders: [ETag]` on the bucket CORS.
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          throw new Error('S3 did not expose the part ETag (bucket CORS)');
        }
        parts.push({ PartNumber: index + 1, ETag: etag });
        loadedByIndex[index] = end - offset;
        reportLoaded();
        return null;
      })
      .catch(err => {
        if (attempt >= PART_ATTEMPTS) {
          throw err;
        }
        // 1s, 2s, 4s — long enough for a flaky uplink to settle.
        return delay(RETRY_BASE_DELAY * 2 ** (attempt - 1)).then(() =>
          uploadPart(index, attempt + 1),
        );
      });
  };

  const worker = () => {
    if (next >= partUrls.length) {
      return Promise.resolve(null);
    }
    const index = next;
    next += 1;
    return uploadPart(index, 1).then(worker);
  };

  const workers = [];
  const concurrency = Math.min(PART_CONCURRENCY, partUrls.length);
  for (let i = 0; i < concurrency; i += 1) {
    workers.push(worker());
  }

  return Promise.all(workers).then(() => {
    // Parts finish out of order (4 in flight); S3 refuses a list that is not
    // ascending. The service sorts as well — this is belt and braces.
    parts.sort((left, right) => left.PartNumber - right.PartNumber);
    return parts;
  });
};

const loadVideoElement = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => resolve({ video, url });
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The browser could not read the video'));
    };
    video.src = url;
  });

/**
 * Duration in whole seconds, 0 when the browser cannot tell.
 *
 * @param {File} file
 * @returns {Promise<number>}
 */
const readVideoDuration = file =>
  loadVideoElement(file)
    .then(loaded => {
      const { duration } = loaded.video;
      URL.revokeObjectURL(loaded.url);
      return Number.isFinite(duration) && duration > 0
        ? Math.round(duration)
        : 0;
    })
    .catch(() => 0);

/**
 * Captures a frame around 0.5s and pushes it through the existing (small,
 * unchanged) `/upload/image` portal endpoint, so the stored poster is a real
 * S3 object exactly like the ffmpeg one was.
 *
 * @param {string} _csrf
 * @param {File} file
 * @returns {Promise<string>} the thumbnail url, or the default placeholder
 */
const captureVideoThumb = (_csrf, file) =>
  loadVideoElement(file)
    .then(loaded => {
      const { video } = loaded;
      return new Promise((resolve, reject) => {
        let settled = false;
        const timeout = setTimeout(() => {
          if (!settled) {
            settled = true;
            reject(new Error('Timed out seeking the video'));
          }
        }, 15000);

        video.onseeked = () => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timeout);
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            if (!canvas.width || !canvas.height) {
              throw new Error('The video has no readable frame');
            }
            canvas
              .getContext('2d')
              .drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
              blob => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('The frame could not be encoded'));
                }
              },
              'image/jpeg',
              0.8,
            );
          } catch (err) {
            reject(err);
          }
        };
        video.onerror = () => {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            reject(new Error('The browser could not read the video'));
          }
        };
        video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
      }).then(
        blob => {
          URL.revokeObjectURL(loaded.url);
          return blob;
        },
        err => {
          URL.revokeObjectURL(loaded.url);
          throw err;
        },
      );
    })
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const form = new FormData();
          form.append('file', blob, 'thumb.jpeg');
          form.append('upload_file', true);
          new Request(_csrf)
            .formMulti('POST', '/upload/image', form)
            .done(result => {
              const image = result ? result.data : null;
              if (image && (image.thumb || image.url)) {
                resolve(image.thumb || image.url);
              } else {
                reject(new Error('The thumbnail upload answered nothing'));
              }
            })
            .fail(err => reject(err));
        }),
    )
    .catch(err => {
      // A missing poster only costs a placeholder — never the upload.
      console.warn('directVideoUpload: thumbnail capture failed', err);
      return DEFAULT_VIDEO_THUMB;
    });

/**
 * Uploads one video straight to S3 and synthesizes the payload the legacy
 * `POST /upload/video` would have answered.
 *
 * NOTE there is no size floor here, unlike the admin portal's general-purpose
 * transport: the whole point is that a small clip must not stage on the public
 * bucket either.
 *
 * @param {string} _csrf
 * @param {File} file the picked file (needs a real `name` — `init` validates
 *   the extension)
 * @param {string} uploadContext one of `VIDEO_UPLOAD_CONTEXT`
 * @returns {Promise<?Object>} `{url, thumb, duration}` (plus
 *   `private`/`receipt`/`previewUrl` when the object landed private), or NULL
 *   when the caller should use the legacy portal upload instead
 */
/**
 * Direct browser -> S3 upload for FILES (BRAVON-6332 Phase 3, Stage 2b).
 *
 * The generic sibling of `uploadVideoDirect`: same init/PUT/complete/abort
 * flow with `kind: 'file'`, no video tail (no poster frame, no duration), and
 * an OPTIONAL context — most documents have none and land wherever the server
 * decides (public bucket, or the private bucket's `public/` namespace with
 * the Phase 3 flag on). `onProgress` reports 0-100 across all parts.
 *
 * Same fail-open contract: every refusal resolves with NULL and the caller
 * falls back to the legacy portal upload.
 */
const uploadFileDirect = (_csrf, file, uploadContext, onProgress) => {
  if (
    !isSupported() ||
    !file ||
    typeof file.name !== 'string' ||
    !file.name ||
    typeof file.size !== 'number'
  ) {
    return Promise.resolve(null);
  }

  const context = uploadContext ? { type: uploadContext } : undefined;
  const state = { requests: [] };
  let uploadId = null;

  const reportProgress = onProgress
    ? loaded =>
        onProgress(
          Math.min(100, Math.round((loaded / (file.size || 1)) * 100)),
        )
    : null;

  const abortUpload = key => {
    if (!uploadId || !key) {
      return Promise.resolve(null);
    }
    const pending = uploadId;
    uploadId = null;
    // Same rule as the video path: the abort must carry the SAME context so
    // the server aborts on the destination init resolved.
    return api(_csrf, ABORT_URL, { key, uploadId: pending, context }).catch(
      () => null,
    );
  };

  const transfer = data => {
    if (data.mode === 'single') {
      return put(
        data.url,
        file,
        data.headers,
        reportProgress,
        state,
      ).then(() => withPrivate({ url: data.publicUrl }, data));
    }
    return uploadParts(file, data, state, reportProgress)
      .then(parts =>
        api(_csrf, COMPLETE_URL, {
          key: data.key,
          uploadId: data.uploadId,
          parts,
          context,
        }),
      )
      .then(result => {
        uploadId = null;
        const source = result && result.private === true ? result : data;
        return withPrivate(
          { url: (result && result.url) || data.publicUrl },
          source,
        );
      });
  };

  return api(_csrf, INIT_URL, {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    kind: 'file',
    context,
  })
    .then(data => {
      if (!data || !data.mode || !data.key) {
        return null;
      }
      uploadId = data.uploadId || null;
      return transfer(data).catch(err =>
        abortUpload(data.key).then(() => {
          throw err;
        }),
      );
    })
    .catch(err => {
      console.warn('directFileUpload: falling back to the portal upload', err);
      return null;
    });
};

const uploadVideoDirect = (_csrf, file, uploadContext) => {
  if (
    !isSupported() ||
    !file ||
    typeof file.name !== 'string' ||
    !file.name ||
    typeof file.size !== 'number' ||
    !uploadContext
  ) {
    return Promise.resolve(null);
  }

  // Held in a closure rather than passed around, so nothing writes through a
  // parameter (`no-param-reassign`) — `put()` only ever pushes/splices the
  // in-flight request list.
  const state = { requests: [] };
  let uploadId = null;

  /**
   * Best effort — the caller is already handling a failure and does not care
   * whether S3 acknowledged the abort. Anything left behind is swept by the
   * bucket's `AbortIncompleteMultipartUpload` lifecycle rule.
   */
  const abortUpload = key => {
    if (!uploadId || !key) {
      return Promise.resolve(null);
    }
    const pending = uploadId;
    uploadId = null;
    // BRAVON-6497: the abort must resolve the SAME destination as init. The
    // server re-derives the bucket from `context` — without it a private
    // upload's abort lands on the public bucket (NoSuchUpload) and the real
    // multipart is left dangling on the private one.
    return api(_csrf, ABORT_URL, {
      key,
      uploadId: pending,
      context: { type: uploadContext },
    }).catch(() => null);
  };

  const transfer = data => {
    if (data.mode === 'single') {
      // A single PUT has no `complete` round trip, so the private fields (when
      // there are any) rode along with `init`.
      return put(data.url, file, data.headers, null, state).then(() =>
        withPrivate({ url: data.publicUrl }, data),
      );
    }
    return uploadParts(file, data, state)
      .then(parts =>
        // BRAVON-6497: same rule as abort — the server re-resolves the bucket
        // from `context`, so a private multipart completed without it targets
        // the PUBLIC bucket and dies with NoSuchUpload.
        api(_csrf, COMPLETE_URL, {
          key: data.key,
          uploadId: data.uploadId,
          parts,
          context: { type: uploadContext },
        }),
      )
      .then(result => {
        uploadId = null;
        // `complete` is where a private multipart upload reports itself;
        // `init` is the fallback so the two modes cannot drift.
        const source = result && result.private === true ? result : data;
        return withPrivate(
          { url: (result && result.url) || data.publicUrl },
          source,
        );
      });
  };

  return api(_csrf, INIT_URL, {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    kind: 'video',
    context: { type: uploadContext },
  })
    .then(data => {
      // Flag off (ERROR_SERVICE_DISABLED), route missing, api_games down,
      // validation refusal — all of them mean "upload it the old way".
      if (!data || !data.mode || !data.key) {
        return null;
      }
      uploadId = data.uploadId || null;
      return transfer(data).catch(err =>
        abortUpload(data.key).then(() => {
          throw err;
        }),
      );
    })
    .then(result => {
      if (!result) {
        return null;
      }
      // The bytes never reach a server that could run ffprobe/ffmpeg, so the
      // duration and the poster frame are produced in the browser.
      return Promise.all([
        readVideoDuration(file),
        captureVideoThumb(_csrf, file),
      ]).then(([duration, thumb]) =>
        withPrivate({ url: result.url, thumb, duration }, result),
      );
    })
    .catch(err => {
      console.warn('directVideoUpload: falling back to the portal upload', err);
      return null;
    });
};

export {
  uploadVideoDirect,
  uploadFileDirect,
  isSupported,
  readVideoDuration,
  captureVideoThumb,
};
export default uploadVideoDirect;
